import { apiClient } from '@/lib/apiClient';

export interface SupplierSession { accessToken: string; name: string; walletBalance: number; currency: string; }
export const login = async (email: string, password: string) =>
  (await apiClient.post<SupplierSession>('/supplier/auth/login', { email, password })).data;

/** Signs in as the shared demo supplier. 404s where the demo isn't enabled (production). */
export const demoLogin = async () =>
  (await apiClient.post<SupplierSession>('/supplier/auth/demo-login')).data;

export interface SupplierMe { id: string; name: string; email: string; status: string; walletBalance: number; currency: string; }
export const getMe = async () => (await apiClient.get<SupplierMe>('/supplier/me')).data;

export interface WalletTxn { date: string; type: string; amount: number; balanceAfter: number; description: string | null; }
export interface Wallet { balance: number; currency: string; transactions: WalletTxn[]; }
export const getWallet = async () => (await apiClient.get<Wallet>('/supplier/wallet')).data;

// Card top-up (Stripe) — the only way to add funds. Returns a PaymentIntent client secret; the
// wallet is credited by the webhook on success, keyed on the intent id so a replay cannot double it.
export interface PayIntent { clientSecret: string; publishableKey: string; }
export const topUpCard = async (amount: number) =>
  (await apiClient.post<PayIntent>('/supplier/wallet/topup/card', { amount })).data;

export interface Creative {
  id: string; title: string; mediaType: string; imageUrl: string; targetUrl: string;
  status: string; reviewNote: string | null; createdAt: string;
}
export const getCreatives = async () => (await apiClient.get<Creative[]>('/supplier/creatives')).data;
export const createCreative = (body: { title: string; mediaType: string; imageUrl: string; targetUrl: string }) =>
  apiClient.post<{ id: string }>('/supplier/creatives', body);

// Direct browser upload of a creative image: get a pre-signed PUT, upload the file, then create with the object path.
export const requestCreativeUpload = async (fileName: string, contentType: string) =>
  (await apiClient.post<{ objectPath: string; uploadUrl: string }>('/supplier/creatives/upload-url', { fileName, contentType })).data;

export const uploadFileToUrl = async (uploadUrl: string, file: File) => {
  const res = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
};

export interface Campaign {
  id: string; name: string; creativeId: string; creativeTitle: string; audience: string; status: string;
  totalBudget: number; spentAmount: number; currency: string; startDate: string; endDate: string;
  impressions: number; clicks: number;
}
export const getCampaigns = async () => (await apiClient.get<Campaign[]>('/supplier/campaigns')).data;
export const createCampaign = (body: {
  name: string; creativeId: string; audience: string; totalBudget: number; startDate: string; endDate: string;
}) => apiClient.post<{ id: string }>('/supplier/campaigns', body);
export const setCampaignStatus = (id: string, status: string) =>
  apiClient.post(`/supplier/campaigns/${id}/status`, { status });

// ── Company details ──────────────────────────────────────────────────────────
//
// The buyer party on advertising invoices. Required before a paid top-up: taking money we cannot
// then invoice back would leave a document we owe and cannot produce.

export interface BillingIdentity {
  legalName: string | null;
  taxId: string | null;
  registrationNumber: string | null;
  address: string | null;
  city: string | null;
  /** ISO 3166-2, e.g. RO-CJ — goes on the e-invoice, so it is typed rather than guessed. */
  countySubdivision: string | null;
  iban: string | null;
  email: string | null;
  isVatRegistered: boolean;
  /**
   * BT-10 — your own reference for our invoices: a purchase order, a cost centre, a contract
   * number. Whatever your accounts-payable system needs quoted back to route it.
   *
   * Yours to give, never ours to invent: a reference we made up would fail to route AND state
   * something untrue on a filed document.
   */
  buyerReference: string | null;
}

export interface SupplierBillingProfile extends BillingIdentity {
  name: string;
  countryCode: string;
  isComplete: boolean;
  missingFields: string[];
}

export const getBillingProfile = async () =>
  (await apiClient.get<SupplierBillingProfile>('/supplier/billing-profile')).data;

export const updateBillingProfile = (identity: BillingIdentity, countryCode: string) =>
  apiClient.put('/supplier/billing-profile', { identity, countryCode });

// ── Offboarding: take your data out, and leave ────────────────────────────────
//
// Two separate things, deliberately not combined into one button. Exporting is routine and
// reversible; closing is neither. A "download and delete" action would make the destructive one a
// side effect of the harmless one.

export interface SupplierExport {
  id: string;
  status: string;            // Pending | Running | Ready | Failed
  requestedAt: string;
  completedAt: string | null;
  sizeBytes: number | null;
  expiresAt: string | null;
}

export const requestExport = async () =>
  (await apiClient.post<{ id: string }>('/supplier/export')).data;

export const getExports = async () =>
  (await apiClient.get<SupplierExport[]>('/supplier/exports')).data;

/**
 * Fetches a short-lived pre-signed URL and navigates to it.
 *
 * ⚠️ Deliberately NOT proxied through the API. The archive can be large, and streaming it through
 * the app server would hold a request open for its whole length; the browser fetches it straight
 * from object storage instead. The URL expires in minutes, so it is fetched at click time rather
 * than rendered into the page.
 */
export const downloadExport = async (exportId: string) => {
  const { url } = (await apiClient.get<{ url: string }>(`/supplier/exports/${exportId}/url`)).data;
  window.location.href = url;
};

export interface SupplierClosureState {
  isClosing: boolean;
  requestedAt: string | null;
  dataDeletedOn: string | null;
  graceDays: number;
  dataAlreadyDeleted: boolean;
}

export const getClosure = async () =>
  (await apiClient.get<SupplierClosureState>('/supplier/closure')).data;

/** `confirmation` must be the company name, typed exactly. The server compares case-sensitively. */
export const requestClosure = async (confirmation: string) =>
  (await apiClient.post<SupplierClosureState>('/supplier/closure', { confirmation })).data;

export const cancelClosure = async () =>
  (await apiClient.delete<SupplierClosureState>('/supplier/closure')).data;
